import React from 'react';
import { LuChevronDown } from 'react-icons/lu';
import { useSearchParams } from 'react-router-dom';

import { AccountType, LifecycleStatus } from '@ndvr/portfolio-api';

import { StatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { formatAccountType, formatDate, getPersonOrgName, titleCase } from 'utils';
import { usePersonOrgProfilesForHousehold } from 'views/person/person-queries';
import {
    AccountGroupProfileWithAumIncluded,
    AccountWithAumIncluded,
    useListAccountGroupProfilesWithAumIncluded,
} from 'views/portfolio/account-aum-queries';
import { isReportableAccount } from 'views/portfolio/reporting-queries';

import { applySelectedAccountGUIDs, readSelectedAccountGUIDs } from './useReportingAccounts';
import { applyIncludeClosed, readIncludeClosed } from './useReportingIncludeClosed';

const INCLUDE_CLOSED_SWITCH_ID = 'reporting-include-closed';

export interface AccountOption {
    // accountGUID
    value: string;
    // Account name — the primary line and the trigger label.
    name: string;
    // Muted secondary line, e.g. "Traditional IRA · Schwab · x2697".
    meta: string;
    // Set for closed accounts so the option can be marked as such; the close timestamp when known.
    closedAt?: string;
    closed: boolean;
    // The account's primary owner (personOrgGUID), when it has one.
    ownerGUID?: string;
    taxable: boolean;
}

export interface OwnerGroup {
    // Unset for the trailing "Other accounts" group.
    ownerGUID?: string;
    ownerName: string;
    options: AccountOption[];
}

export type TaxFilter = 'all' | 'taxable' | 'nonTaxable';

const TAX_FILTERS: TaxFilter[] = ['all', 'taxable', 'nonTaxable'];

const TAX_FILTER_LABELS: Record<TaxFilter, string> = { all: 'All', taxable: 'Taxable', nonTaxable: 'Non-Taxable' };

export const OTHER_ACCOUNTS_GROUP_NAME = 'Other accounts';

// The account's type, custodian and masked custody id, joined into one muted metadata line —
// mirrors how accounts are labelled elsewhere (OverviewTab AccountRow uses formatAccountType).
// Closing an account clears its custody link, so a closed account carries its close date instead.
const accountMeta = (account: AccountWithAumIncluded): string => {
    const parts = [formatAccountType(account.accountType)];
    if (account.custodyType) {
        parts.push(titleCase(account.custodyType));
    }
    if (account.custodyAccountId) {
        parts.push(`x${account.custodyAccountId.slice(-4)}`);
    }
    if (account.lifecycleStatus === LifecycleStatus.CLOSED && account.closedAt) {
        parts.push(`Closed ${formatDate(account.closedAt, 'HUMAN')}`);
    }
    return parts.join(' · ');
};

// Flattens the household's account groups into a de-duplicated list of account options — an
// account shared across portfolios appears only once (keyed by accountGUID). Filtering is per
// account, not per group: a group's status is only the highest status among its accounts, so a
// live group can still hold drafts or closed accounts. Only REAL accounts (plus CLOSED ones when
// asked for) carry report data.
export const toAccountOptions = (
    profiles: AccountGroupProfileWithAumIncluded[] | undefined,
    includeClosed: boolean,
): AccountOption[] => {
    const seen = new Set<string>();
    const options: AccountOption[] = [];
    for (const profile of profiles ?? []) {
        for (const account of profile.accounts) {
            if (!isReportableAccount(account, includeClosed) || seen.has(account.accountGUID)) {
                continue;
            }
            seen.add(account.accountGUID);
            options.push({
                value: account.accountGUID,
                name: account.accountName?.plainText ?? 'Unnamed account',
                meta: accountMeta(account),
                closed: account.lifecycleStatus === LifecycleStatus.CLOSED,
                closedAt: account.closedAt,
                ownerGUID: account.ownerPersonOrgGUID,
                taxable: account.accountType === AccountType.TAXABLE,
            });
        }
    }
    return options;
};

// Groups the options under their primary owner, owners in first-seen order. Accounts without an
// owner, or whose owner is not among the household's people, go into a trailing "Other accounts"
// group. Groups only exist for owners that have at least one option, so none is ever empty.
export const groupByOwner = (options: AccountOption[], ownerNames: ReadonlyMap<string, string>): OwnerGroup[] => {
    const groups = new Map<string | undefined, OwnerGroup>();
    for (const option of options) {
        const ownerName = option.ownerGUID ? ownerNames.get(option.ownerGUID) : undefined;
        const ownerGUID = ownerName ? option.ownerGUID : undefined;
        const group = groups.get(ownerGUID) ?? {
            ownerGUID,
            ownerName: ownerName ?? OTHER_ACCOUNTS_GROUP_NAME,
            options: [],
        };
        group.options.push(option);
        groups.set(ownerGUID, group);
    }
    const other = groups.get(undefined);
    groups.delete(undefined);
    return other ? [...groups.values(), other] : [...groups.values()];
};

// The accounts a tax filter lists: every account, only TAXABLE ones, or every other type.
export const optionsForTaxFilter = (options: AccountOption[], filter: TaxFilter): AccountOption[] =>
    options.filter((option) => filter === 'all' || option.taxable === (filter === 'taxable'));

// The filter whose accounts are exactly the given selection, so the dropdown can reopen on the
// filter it was committed with; undefined for a hand-picked selection or an empty one. When two
// filters list the same accounts (e.g. every account is taxable), 'all' wins as the first in TAX_FILTERS.
export const activeTaxFilter = (options: AccountOption[], selection: string[]): TaxFilter | undefined => {
    if (selection.length === 0) {
        return undefined;
    }
    const selected = new Set(selection);
    return TAX_FILTERS.find((filter) => {
        const matching = optionsForTaxFilter(options, filter);
        return matching.length === selected.size && matching.every((option) => selected.has(option.value));
    });
};

// "All accounts" is an empty selection (an empty groupByGuids is the household total), so checking
// every account by hand commits the same empty selection.
export const toCommittedSelection = (options: AccountOption[], staged: string[]): string[] =>
    activeTaxFilter(options, staged) === 'all' ? [] : staged;

// Everything the popover edits before Done, seeded from the URL each time it opens.
export interface StagedSelection {
    filter: TaxFilter;
    // Every checked account, including ones the filter currently hides: the chips only filter, so
    // switching back shows them still checked. Done commits only the listed ones.
    checked: string[];
    includeClosed: boolean;
}

// Seeds the popover from the committed selection. Nothing checked means "everything the filter lists",
// so a selection that is exactly one filter's accounts (including "All accounts", the empty one)
// reopens on that filter with nothing checked; any other selection reopens on All with it checked.
// `options` must be built with the same includeClosed.
export const toStagedSelection = (
    options: AccountOption[],
    committed: string[],
    includeClosed: boolean,
): StagedSelection => {
    const filter = activeTaxFilter(options, committed);
    return filter ? { filter, checked: [], includeClosed } : { filter: 'all', checked: committed, includeClosed };
};

// What Done commits: the checked accounts the filter lists, or everything it lists when none of them
// is checked; every account comes out as [] ("All accounts"). Undefined when the filter lists nothing,
// since committing [] would then select the whole household. `options` must be built with the staged
// includeClosed.
export const commitSelection = (options: AccountOption[], staged: StagedSelection): string[] | undefined => {
    const listed = optionsForTaxFilter(options, staged.filter).map((option) => option.value);
    if (listed.length === 0) {
        return undefined;
    }
    const checked = staged.checked.filter((accountGUID) => listed.includes(accountGUID));
    return toCommittedSelection(options, checked.length > 0 ? checked : listed);
};

// Checked state of an owner row: all, some or none of its accounts.
const ownerCheckState = (accountGUIDs: string[], checked: string[]): boolean | 'indeterminate' => {
    const checkedCount = accountGUIDs.filter((accountGUID) => checked.includes(accountGUID)).length;
    if (checkedCount === 0) {
        return false;
    }
    return checkedCount === accountGUIDs.length ? true : 'indeterminate';
};

/**
 * The reporting account filter: tax chips, the household's accounts grouped under their primary
 * owner, and the closed-account toggle. Every change is staged in the popover and committed to the
 * URL only on Done (avoids a refetch on every toggle), so it owns the account and closed-account
 * params itself rather than taking them as props.
 */
export const ReportingAccountSelector = ({ householdGUID }: { householdGUID: string }) => {
    const [params, setParams] = useSearchParams();
    // The committed selection; [] is "All accounts".
    const value = readSelectedAccountGUIDs(params);
    const includeClosed = readIncludeClosed(params);
    const { data: profiles, isPending: isProfilesPending } = useListAccountGroupProfilesWithAumIncluded(householdGUID);
    const { data: people, isPending: isPeoplePending } = usePersonOrgProfilesForHousehold(householdGUID);

    const [open, setOpen] = React.useState(false);
    const [staged, setStaged] = React.useState<StagedSelection>({ filter: 'all', checked: [], includeClosed });

    const options = React.useMemo(
        () => toAccountOptions(profiles, staged.includeClosed),
        [profiles, staged.includeClosed],
    );
    // Names for the trigger come from every reportable account, closed ones included, so a committed
    // closed account keeps its name whatever the staged toggle says.
    const nameByValue = React.useMemo(
        () => new Map(toAccountOptions(profiles, true).map((o) => [o.value, o.name])),
        [profiles],
    );
    const ownerNames = React.useMemo(
        () => new Map((people ?? []).map((person) => [person.personOrgGUID, getPersonOrgName(person.personOrg)])),
        [people],
    );
    // The popover is seeded from the account list when it opens, and without owner names every account
    // would land in "Other accounts", so the trigger waits for both queries.
    const isLoading = isProfilesPending || isPeoplePending;

    const listed = optionsForTaxFilter(options, staged.filter);
    const groups = groupByOwner(listed, ownerNames);
    const listedGUIDs = listed.map((option) => option.value);
    const checked = staged.checked.filter((accountGUID) => listedGUIDs.includes(accountGUID));
    const selection = commitSelection(options, staged);

    const handleOpenChange = (next: boolean) => {
        if (next) {
            setStaged(toStagedSelection(toAccountOptions(profiles, includeClosed), value, includeClosed));
        }
        setOpen(next);
    };

    const toggleAccounts = (accountGUIDs: string[]) =>
        setStaged((prev) => ({
            ...prev,
            checked:
                ownerCheckState(accountGUIDs, checked) === true
                    ? prev.checked.filter((accountGUID) => !accountGUIDs.includes(accountGUID))
                    : [...new Set([...prev.checked, ...accountGUIDs])],
        }));

    // Selection and closed toggle go out in one URL update: useSearchParams setters each navigate
    // from their own render-time snapshot, so two setter calls would race and the last would win.
    const done = () => {
        if (!selection) {
            return;
        }
        const updated = applyIncludeClosed(new URLSearchParams(params), staged.includeClosed);
        setParams(applySelectedAccountGUIDs(updated, selection), { replace: true });
        setOpen(false);
    };

    // Trigger shows the first selected account's name; the count lives in the corner badge.
    const triggerLabel = value.length > 0 ? (nameByValue.get(value[0]) ?? 'Selected account') : 'All accounts';

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <span className="relative inline-flex">
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        aria-label={value.length > 0 ? `${value.length} accounts selected` : 'All accounts'}
                        disabled={isLoading}
                        className="w-56 justify-start font-normal"
                    >
                        <LuChevronDown className="mr-2 h-4 w-4 opacity-50" />
                        <span className="truncate">{triggerLabel}</span>
                    </Button>
                </PopoverTrigger>
                {value.length > 0 ? (
                    <Badge className="pointer-events-none absolute -top-1.5 -right-1.5 h-5 min-w-5 justify-center px-1">
                        {value.length}
                    </Badge>
                ) : null}
            </span>
            <PopoverContent className="w-80 p-0" align="end">
                <div className="border-b p-2">
                    {/* NetValueCard's segmented control; items stretch to share the row (no flex-none)
                        and use px-2 so "Non-Taxable" fits. */}
                    <ToggleGroup
                        type="single"
                        aria-label="Account tax treatment"
                        value={staged.filter}
                        onValueChange={(next) => {
                            const filter = TAX_FILTERS.find((item) => item === next);
                            if (filter) setStaged((prev) => ({ ...prev, filter }));
                        }}
                        className="flex w-full rounded-xl bg-stone-200/70 p-1"
                    >
                        {TAX_FILTERS.map((filter) => (
                            <ToggleGroupItem
                                key={filter}
                                value={filter}
                                className="text-cocoa-600 data-[state=on]:text-cocoa-1000 h-auto cursor-pointer rounded-lg px-2 py-2 font-medium first:rounded-lg last:rounded-lg hover:bg-white/40 data-[state=on]:bg-white data-[state=on]:shadow-sm"
                            >
                                {TAX_FILTER_LABELS[filter]}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                </div>
                <Command>
                    <CommandList className="max-h-72">
                        <CommandEmpty>No accounts available.</CommandEmpty>
                        {groups.map((group) => {
                            const accountGUIDs = group.options.map((option) => option.value);
                            return (
                                <CommandGroup key={group.ownerGUID ?? OTHER_ACCOUNTS_GROUP_NAME}>
                                    <CommandItem
                                        value={`owner:${group.ownerGUID ?? OTHER_ACCOUNTS_GROUP_NAME}`}
                                        onSelect={() => toggleAccounts(accountGUIDs)}
                                        className="group"
                                    >
                                        <Checkbox
                                            checked={ownerCheckState(accountGUIDs, checked)}
                                            className="pointer-events-none"
                                        />
                                        <span className="text-cocoa-900 truncate text-sm font-medium group-data-[selected=true]:text-white">
                                            {group.ownerName}
                                        </span>
                                    </CommandItem>
                                    {group.options.map((option) => (
                                        <CommandItem
                                            key={option.value}
                                            value={option.value}
                                            onSelect={() => toggleAccounts([option.value])}
                                            className="group pl-8"
                                        >
                                            <Checkbox
                                                checked={checked.includes(option.value)}
                                                className="pointer-events-none"
                                            />
                                            <div className="flex min-w-0 flex-col">
                                                <span className="flex min-w-0 items-center gap-2">
                                                    <span className="text-cocoa-900 truncate text-sm group-data-[selected=true]:text-white">
                                                        {option.name}
                                                    </span>
                                                    {option.closed ? (
                                                        <StatusBadge
                                                            status={LifecycleStatus.CLOSED}
                                                            closedAt={option.closedAt}
                                                            className="shrink-0 px-1.5 py-0 text-[10px]"
                                                        />
                                                    ) : null}
                                                </span>
                                                <span className="text-cocoa-400 truncate text-xs group-data-[selected=true]:text-white/70">
                                                    {option.meta}
                                                </span>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            );
                        })}
                    </CommandList>
                </Command>
                <div className="flex items-center gap-2 border-t px-3 py-2.5">
                    <Switch
                        id={INCLUDE_CLOSED_SWITCH_ID}
                        checked={staged.includeClosed}
                        onCheckedChange={(next) => setStaged((prev) => ({ ...prev, includeClosed: next }))}
                    />
                    <Label htmlFor={INCLUDE_CLOSED_SWITCH_ID} className="text-cocoa-500 text-sm font-normal">
                        Show closed accounts
                    </Label>
                </div>
                <div className="flex items-center gap-2 border-t p-2">
                    {/* Back to the default view: All, nothing checked. */}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1"
                        onClick={() => setStaged((prev) => ({ ...prev, filter: 'all', checked: [] }))}
                    >
                        Clear
                    </Button>
                    <Button size="sm" className="flex-1" disabled={!selection} onClick={done}>
                        Done
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
};
